import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { sendOrderConfirmation } from "@/lib/order-confirmation.functions";

export const Route = createFileRoute("/_authenticated/prijzen")({
  head: () => ({ meta: [{ title: "Prijslijst" }, { name: "robots", content: "noindex" }] }),
  component: PricesPage,
});

type CartItem = { id: string; name: string; price_cents: number; qty: number };

function PricesPage() {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const [cart, setCart] = useState<CartItem[]>([]);

  const { data: products, isLoading } = useQuery({
    queryKey: ["products"],
    enabled: profile?.verification_status === "approved",
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id,name,description,price_cents,unit,category,is_active,slug,brand,sku,stock,image_url,images,seo_title,seo_description,pros,cons,specifications,updated_at")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const sendConfirmation = useServerFn(sendOrderConfirmation);

  const placeOrder = useMutation({
    mutationFn: async () => {
      if (cart.length === 0) throw new Error("Lege bestelling");
      const total = cart.reduce((s, i) => s + i.price_cents * i.qty, 0);
      const orderNumber = `ORD-${Date.now()}`;
      const { data, error } = await supabase.from("orders").insert({
        user_id: profile!.id,
        order_number: orderNumber,
        line_items: cart,
        total_cents: total,
      }).select("id").single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (orderId) => {
      toast.success("Bestelling geplaatst — bevestiging onderweg");
      setCart([]);
      qc.invalidateQueries({ queryKey: ["my-orders"] });
      sendConfirmation({ data: { orderId } }).catch((err: any) =>
        console.warn("[orders] confirmation email skipped:", err?.message ?? err),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (profile?.verification_status !== "approved") {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Prijzen alleen voor goedgekeurde klanten</h1>
        <p className="text-muted-foreground">
          {profile?.verification_status === "pending"
            ? "Je zakelijke account wordt nog gecontroleerd."
            : "Je account moet eerst worden goedgekeurd."}
        </p>
        <Link to="/account" className="mt-4 inline-block text-primary hover:underline">Terug naar account</Link>
      </div>
    );
  }

  const add = (p: NonNullable<typeof products>[number]) => {
    setCart((c) => {
      const found = c.find((i) => i.id === p.id);
      if (found) return c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { id: p.id, name: p.name, price_cents: p.price_cents, qty: 1 }];
    });
  };

  const total = cart.reduce((s, i) => s + i.price_cents * i.qty, 0);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div>
        <h1 className="text-2xl font-bold mb-4">Prijslijst</h1>
        {isLoading && <p>Laden…</p>}
        {products?.length === 0 && <p className="text-muted-foreground">Nog geen producten beschikbaar.</p>}
        <div className="space-y-2">
          {products?.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border bg-card p-4">
              <div>
                <div className="font-semibold">{p.name}</div>
                {p.description && <div className="text-sm text-muted-foreground">{p.description}</div>}
              </div>
              <div className="flex items-center gap-3">
                <div className="font-semibold">€ {(p.price_cents / 100).toFixed(2)}{p.unit ? ` /${p.unit}` : ""}</div>
                <Button size="sm" onClick={() => add(p)}>+</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <aside className="rounded-lg border bg-card p-4 h-fit sticky top-20">
        <h2 className="font-semibold mb-3">Winkelmand</h2>
        {cart.length === 0 ? (
          <p className="text-sm text-muted-foreground">Leeg</p>
        ) : (
          <>
            <ul className="space-y-2 text-sm">
              {cart.map((i) => (
                <li key={i.id} className="flex justify-between">
                  <span>{i.name} × {i.qty}</span>
                  <span>€ {((i.price_cents * i.qty) / 100).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between border-t pt-3 font-semibold">
              <span>Totaal</span><span>€ {(total / 100).toFixed(2)}</span>
            </div>
            <Button className="mt-3 w-full" onClick={() => placeOrder.mutate()} disabled={placeOrder.isPending}>Bestelling plaatsen</Button>
            <Button variant="ghost" size="sm" className="mt-1 w-full" onClick={() => setCart([])}>Leegmaken</Button>
          </>
        )}
      </aside>
    </div>
  );
}
